import React, { useState, useEffect } from 'react';
import axios from 'axios';
import '../../barcodeform.css';
import * as XLSX from 'xlsx';
import { getApiBaseUrl } from '../../utils/utils';

// Per-partner Same/Next-day bill generation + rate config.
// Mirrors AccountsReport.js conventions:
//   - API_URL base = process.env.REACT_APP_API_URL (already ends with '/'), paths append 'shypvdriverapp/...'
//   - partner dropdown from CRM: `${getApiBaseUrl()}/v1.0/partner/retrieve_list?page=0` -> data[] { pid, pname }
//   - xlsx/csv export via XLSX.utils.json_to_sheet
export default function GeneratePartnerBill() {

    const API_URL = process.env.REACT_APP_API_URL;

    const buttonsyle = {
        background: '#00a6ed',
        color: '#fff',
        padding: '3px',
        marginRight: '10px',
        borderColor: '#00a6ed',
        cursor: 'pointer',
        marginTop: '3%'
    };

    const [partners, setPartners] = useState([]);
    const [selectedPartner, setSelectedPartner] = useState('0');
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [exportFormat, setExportFormat] = useState('excel'); // 'excel' | 'csv'

    const [bill, setBill] = useState(null);
    const [loading, setLoading] = useState(false);
    const [errormsg, setError] = useState('');

    // Rate config
    const [showRates, setShowRates] = useState(false);
    const [rateRows, setRateRows] = useState([]);
    const [rateMsg, setRateMsg] = useState('');
    const [rateError, setRateError] = useState('');

    // Load partner dropdown (same source as AccountsReport)
    useEffect(() => {
        const fetchPartners = async () => {
            const baseApiUrl = getApiBaseUrl();
            try {
                const response = await axios.get(`${baseApiUrl}/v1.0/partner/retrieve_list?page=0`);
                setPartners(response.data.data || []);
            } catch (err) {
                setError(err.message || 'Error fetching partners');
            }
        };
        fetchPartners();
        loadRates();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const partnerNameOf = (pid) => {
        const p = partners.find((x) => String(x.pid) === String(pid));
        return p ? p.pname : '';
    };

    const fmt = (n) => (Number(n || 0)).toFixed(2);

    const handleSelectChange = (e) => {
        setSelectedPartner(e.target.value);
    };

    // ── Rate config ───────────────────────────────────────────────
    const loadRates = async () => {
        try {
            const res = await axios.get(`${API_URL}shypvdriverapp/finance/partner-rates`);
            if (res.data && res.data.success && Array.isArray(res.data.data)) {
                setRateRows(res.data.data.map((r) => ({
                    partnerId: r.partnerId,
                    partnerName: r.partnerName || '',
                    sameDayRate: r.sameDayRate != null ? r.sameDayRate : 0,
                    nextDayRate: r.nextDayRate != null ? r.nextDayRate : 0,
                    currency: r.currency || 'AED',
                    enabled: r.enabled !== false,
                })));
            }
        } catch (err) {
            setRateError('Failed to load partner rates');
        }
    };

    // Make sure the currently-selected partner always has an editable row so a rate
    // can be set for it even if no partner_service_rate exists yet.
    useEffect(() => {
        if (!showRates) return;
        if (!selectedPartner || selectedPartner === '0') return;
        setRateRows((prev) => {
            if (prev.some((r) => String(r.partnerId) === String(selectedPartner))) return prev;
            return [{
                partnerId: Number(selectedPartner),
                partnerName: partnerNameOf(selectedPartner),
                sameDayRate: 0,
                nextDayRate: 0,
                currency: 'AED',
                enabled: true,
            }, ...prev];
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showRates, selectedPartner, partners]);

    const updateRateField = (partnerId, field, value) => {
        setRateRows((prev) => prev.map((r) => (
            String(r.partnerId) === String(partnerId) ? { ...r, [field]: value } : r
        )));
    };

    const saveRate = async (row) => {
        setRateMsg('');
        setRateError('');
        try {
            const body = {
                partnerId: Number(row.partnerId),
                partnerName: row.partnerName || partnerNameOf(row.partnerId) || undefined,
                sameDayRate: Number(row.sameDayRate) || 0,
                nextDayRate: Number(row.nextDayRate) || 0,
                currency: row.currency || 'AED',
                enabled: row.enabled !== false,
            };
            const res = await axios.post(
                `${API_URL}shypvdriverapp/finance/partner-rates`,
                body,
                { headers: { 'Content-Type': 'application/json' } }
            );
            if (res.data && res.data.success) {
                setRateMsg(`Saved rates for ${body.partnerName || 'partner ' + body.partnerId}.`);
            } else {
                setRateError((res.data && res.data.message) || 'Failed to save rate');
            }
        } catch (err) {
            setRateError('An error occurred while saving the rate');
        }
    };

    // ── Bill generation ───────────────────────────────────────────
    const generateBill = async () => {
        setError('');
        if (!selectedPartner || selectedPartner === '0') {
            setError('Please select a partner.');
            return;
        }
        if (!fromDate || !toDate) {
            setError('Please choose both From and To dates.');
            return;
        }
        setLoading(true);
        try {
            const res = await axios.post(
                `${API_URL}shypvdriverapp/finance/generate-bill`,
                { partnerId: Number(selectedPartner), fromDate, toDate },
                { headers: { 'Content-Type': 'application/json' } }
            );
            if (res.data && res.data.success) {
                setBill(res.data.data || null);
                if (res.data.data && res.data.data.ratesConfigured === false) {
                    setShowRates(true); // auto-open rate config so the user can fix it
                }
            } else {
                setBill(null);
                setError((res.data && res.data.message) || 'Failed to generate bill');
            }
        } catch (err) {
            setBill(null);
            setError('An error occurred while generating the bill');
        } finally {
            setLoading(false);
        }
    };

    // ── Export (mirror AccountsReport json_to_sheet pattern) ──────
    const buildExportRows = () => {
        const rows = (bill.lineItems || []).map((li) => ({
            'AWB': li.awb || '',
            'Cust AWB': li.custAwb || '',
            'City': li.city || '',
            'Placed Date': li.placedDate || '',
            'Delivered Date': li.deliveredDate || '',
            'Service': li.serviceClass || '',
            'Weight': li.weight != null ? li.weight : '',
            'Rate': li.rate != null ? li.rate : 0,
            'Amount': li.amount != null ? li.amount : 0,
            'Status': li.status || '',
        }));
        // Summary rows at the end
        rows.push({ 'AWB': '', 'Service': `Same Day (${bill.sameDayCount || 0} x ${fmt(bill.sameDayRate)})`, 'Amount': bill.sameDayTotal != null ? bill.sameDayTotal : 0 });
        rows.push({ 'AWB': '', 'Service': `Next Day (${bill.nextDayCount || 0} x ${fmt(bill.nextDayRate)})`, 'Amount': bill.nextDayTotal != null ? bill.nextDayTotal : 0 });
        rows.push({ 'AWB': '', 'Service': `GRAND TOTAL (${bill.currency || 'AED'})`, 'Amount': bill.grandTotal != null ? bill.grandTotal : 0 });
        return rows;
    };

    const downloadBill = () => {
        if (!bill || !bill.lineItems || bill.lineItems.length === 0) {
            setError('Nothing to export.');
            return;
        }
        const shaped = buildExportRows();
        const ws = XLSX.utils.json_to_sheet(shaped);
        const partnerLabel = (bill.partnerName || partnerNameOf(selectedPartner) || selectedPartner || 'partner')
            .toString().replace(/[^\w-]+/g, '_');
        const baseName = `Bill_${partnerLabel}_${bill.fromDate || fromDate}_${bill.toDate || toDate}`;
        if (exportFormat === 'csv') {
            const csv = XLSX.utils.sheet_to_csv(ws);
            const blob = new Blob(["﻿" + csv], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url; a.download = `${baseName}.csv`; a.click();
            URL.revokeObjectURL(url);
        } else {
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Bill');
            XLSX.writeFile(wb, `${baseName}.xlsx`);
        }
    };

    const ratesConfigured = bill ? bill.ratesConfigured !== false : true;
    const currency = (bill && bill.currency) || 'AED';

    return (
        <div>
            <div className="form-container">
                <h4>Generate Partner Bill</h4>

                <div className="row">
                    <div className="col-md-8">
                        <div className="row">
                            <div className="col-md-4">
                                <div className="form-group">
                                    <label htmlFor="fromDate">From Date:</label>
                                    <input
                                        type="date"
                                        id="fromDate"
                                        value={fromDate}
                                        onChange={(e) => setFromDate(e.target.value)}
                                    />
                                </div>
                            </div>
                            <div className="col-md-4">
                                <div className="form-group">
                                    <label htmlFor="toDate">To Date:</label>
                                    <input
                                        type="date"
                                        id="toDate"
                                        value={toDate}
                                        onChange={(e) => setToDate(e.target.value)}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="row">
                            <div className="col-md-8">
                                <div className="form-group">
                                    <label htmlFor="partner" className="m-form">Partner*</label>
                                    <select id="partner" onChange={handleSelectChange} value={selectedPartner} style={{ marginRight: '10px' }}>
                                        <option value="0">-- Select a partner --</option>
                                        {partners && [...partners].sort((a, b) => a.pname.localeCompare(b.pname)).map((partner) => (
                                            <option key={partner.pid} value={partner.pid}>
                                                {String(partner.pid).padStart(2, '0')} - {partner.pname}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>

                        <div className="row">
                            <div className="col-md-3"></div>
                            <div className="col-md-6">
                                <a onClick={generateBill} style={buttonsyle} type="button" className="btn-demo add">
                                    Generate Bill
                                </a>
                                <a
                                    onClick={() => { setShowRates((v) => !v); setRateMsg(''); setRateError(''); }}
                                    style={{ ...buttonsyle, background: '#6c757d', borderColor: '#6c757d' }}
                                    type="button"
                                    className="btn-demo"
                                >
                                    {showRates ? 'Hide Rates' : 'Manage Rates'}
                                </a>
                            </div>
                        </div>
                        <p style={{ color: 'red', marginTop: '2%' }}>{errormsg}</p>
                    </div>

                    <div className="col-md-4">
                        {bill && bill.lineItems && bill.lineItems.length > 0 && (
                            <div className="text-right">
                                <div className="inline-block" style={{ marginRight: '12px', marginBottom: '5px' }}>
                                    <label style={{ fontSize: 12, marginRight: 4, color: '#555' }}>Format:</label>
                                    <select value={exportFormat} onChange={(e) => setExportFormat(e.target.value)}
                                        style={{ fontSize: 12, padding: '2px 6px' }}>
                                        <option value="excel">Excel (.xlsx)</option>
                                        <option value="csv">CSV (.csv)</option>
                                    </select>
                                </div>
                                <div className="inline-block" style={{ marginRight: '10px', marginBottom: '5px' }}>
                                    <button type="button" className="btn btn-link text-primary font-weight-bold p-0"
                                        onClick={downloadBill}>
                                        <i className="fa fa-download" /> Download Bill
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {loading && <p>Generating bill...</p>}

                {/* ── Summary panel ── */}
                {bill && (
                    <div className="row" style={{ marginTop: '2%' }}>
                        <div className="col-md-11">
                            {!ratesConfigured && (
                                <div style={{
                                    background: '#fff3cd', border: '1px solid #ffeeba', color: '#856404',
                                    padding: '10px', borderRadius: '4px', marginBottom: '10px'
                                }}>
                                    <strong>No rates set for this partner</strong> — set them below (amounts shown as 0 until then).
                                </div>
                            )}

                            <h4 style={{ textAlign: 'left' }}>
                                Bill Summary: {bill.partnerName || partnerNameOf(selectedPartner)}
                                <span style={{ fontSize: 13, color: '#555', fontWeight: 'normal' }}>
                                    {'  '}({bill.fromDate} to {bill.toDate})
                                </span>
                            </h4>

                            <table className="table table-bordered" style={{ maxWidth: 560 }}>
                                <thead>
                                    <tr style={{ background: '#5583af59' }}>
                                        <th>Service</th>
                                        <th>Count</th>
                                        <th>Rate</th>
                                        <th>Total ({currency})</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>Same Day</td>
                                        <td>{bill.sameDayCount || 0}</td>
                                        <td>{fmt(bill.sameDayRate)}</td>
                                        <td>{fmt(bill.sameDayTotal)}</td>
                                    </tr>
                                    <tr>
                                        <td>Next Day</td>
                                        <td>{bill.nextDayCount || 0}</td>
                                        <td>{fmt(bill.nextDayRate)}</td>
                                        <td>{fmt(bill.nextDayTotal)}</td>
                                    </tr>
                                    <tr style={{ fontWeight: 'bold', background: '#eef5ff' }}>
                                        <td colSpan="3" style={{ textAlign: 'right' }}>GRAND TOTAL ({currency})</td>
                                        <td>{fmt(bill.grandTotal)}</td>
                                    </tr>
                                </tbody>
                            </table>

                            {bill.excludedNoTimestampCount > 0 && (
                                <p style={{ fontSize: 12, color: '#8a6d3b' }}>
                                    Note: {bill.excludedNoTimestampCount} delivered order(s) excluded — no usable delivery timestamp.
                                </p>
                            )}
                        </div>
                    </div>
                )}

                {/* ── Line-item table ── */}
                {bill && bill.lineItems && bill.lineItems.length > 0 && (
                    <div className="row">
                        <div className="col-md-11">
                            <div className="shipdata" style={{
                                background: '#5583af59',
                                maxHeight: '500px',
                                overflowY: 'auto',
                                border: '1px solid #ccc'
                            }}>
                                <table className="table table-bordered table-striped">
                                    <thead>
                                        <tr>
                                            <th>AWB</th>
                                            <th>Cust AWB</th>
                                            <th>City</th>
                                            <th>Placed Date</th>
                                            <th>Delivered Date</th>
                                            <th>Service</th>
                                            <th>Weight</th>
                                            <th>Rate</th>
                                            <th>Amount</th>
                                            <th>Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {bill.lineItems.map((li, index) => (
                                            <tr key={index}>
                                                <td>{li.awb}</td>
                                                <td>{li.custAwb}</td>
                                                <td>{li.city}</td>
                                                <td>{li.placedDate}</td>
                                                <td>{li.deliveredDate}</td>
                                                <td>
                                                    <span style={{
                                                        display: 'inline-block',
                                                        padding: '1px 6px',
                                                        borderRadius: '10px',
                                                        fontSize: '11px',
                                                        color: '#fff',
                                                        backgroundColor: li.serviceClass === 'Same Day' ? '#28a745' : '#fd7e14'
                                                    }}>
                                                        {li.serviceClass}
                                                    </span>
                                                </td>
                                                <td>{li.weight}</td>
                                                <td>{fmt(li.rate)}</td>
                                                <td>{fmt(li.amount)}</td>
                                                <td>{li.status}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot style={{ background: '#fff' }}>
                                        <tr style={{ fontWeight: 'bold' }}>
                                            <td colSpan="8" style={{ textAlign: 'right' }}>GRAND TOTAL ({currency}):</td>
                                            <td>{fmt(bill.grandTotal)}</td>
                                            <td></td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        </div>
                    </div>
                )}

                {bill && bill.lineItems && bill.lineItems.length === 0 && (
                    <p style={{ marginTop: '2%' }}>No delivered orders found for this partner in the selected range.</p>
                )}

                {/* ── Rate config section ── */}
                {showRates && (
                    <div className="row" style={{ marginTop: '3%' }}>
                        <div className="col-md-11">
                            <h4 style={{ textAlign: 'left' }}>Partner Same/Next-Day Rates</h4>
                            <p style={{ fontSize: 12, color: '#555' }}>
                                Set the Same Day and Next Day rate per partner. Saving upserts the rate (keyed by partner).
                            </p>
                            {rateMsg && <p style={{ color: 'green' }}>{rateMsg}</p>}
                            {rateError && <p style={{ color: 'red' }}>{rateError}</p>}
                            <table className="table table-bordered table-striped">
                                <thead>
                                    <tr>
                                        <th>Partner</th>
                                        <th>Same Day Rate</th>
                                        <th>Next Day Rate</th>
                                        <th>Currency</th>
                                        <th>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rateRows.length === 0 && (
                                        <tr><td colSpan="5">No rates configured. Select a partner above to add one.</td></tr>
                                    )}
                                    {rateRows.map((row) => (
                                        <tr key={row.partnerId}>
                                            <td>
                                                {String(row.partnerId).padStart(2, '0')} - {row.partnerName || partnerNameOf(row.partnerId)}
                                            </td>
                                            <td>
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    style={{ width: 110 }}
                                                    value={row.sameDayRate}
                                                    onChange={(e) => updateRateField(row.partnerId, 'sameDayRate', e.target.value)}
                                                />
                                            </td>
                                            <td>
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    style={{ width: 110 }}
                                                    value={row.nextDayRate}
                                                    onChange={(e) => updateRateField(row.partnerId, 'nextDayRate', e.target.value)}
                                                />
                                            </td>
                                            <td>
                                                <input
                                                    type="text"
                                                    style={{ width: 70 }}
                                                    value={row.currency}
                                                    onChange={(e) => updateRateField(row.partnerId, 'currency', e.target.value)}
                                                />
                                            </td>
                                            <td>
                                                <button type="button" className="btn btn-link text-primary font-weight-bold p-0"
                                                    onClick={() => saveRate(row)}>
                                                    <i className="fa fa-save" /> Save
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
